// TGCLOUD-1818 — the chip row that replaces the "All fields" checkbox picker.
//
// The picker could not express either of the two searches users actually do,
// because both are conjunctions of two values against two different fields:
// "find this person in this family" (family ID AND first name) and "find which
// family this person is in" (name AND date of birth). It also hid the state
// that determined the result set behind a click, which produced "why am I
// getting no results" with nothing on screen to explain it.
//
// Chips fix both: one criterion each, always visible, click to edit, cross to
// remove, ANDed. The free-text box is untouched and sits where it always did.

import React from 'react';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import { styled, alpha } from '@mui/material/styles';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import FilterChip from './FilterChip';
import FilterBuilder from './FilterBuilder';
import {
  OPERATOR_LABEL_KEYS,
  criterionSentence,
  fieldLabel as resolveFieldLabel,
  truncateValue,
  valueText
} from './filterLabels';

/** Chips shown before the rest collapse behind a "+N more". */
export const OVERFLOW_AFTER = 4;

const Row = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  width: '100%',
  // EXPLICIT, and load-bearing. This app mounts no <CssBaseline/> and
  // src/App.css sets box-sizing on html/body only — box-sizing does not
  // inherit, so descendants are content-box. `width: 100%` plus 44px of side
  // padding therefore overflows the toolbar by 44px, and `body { overflow:
  // hidden }` in App.css means that shows up as CLIPPED chips rather than a
  // scrollbar. Measured at 1440/1280/1024/900 before and after.
  boxSizing: 'border-box',
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(2.5),
  paddingBottom: theme.spacing(1)
}));

const AddButton = styled(Button)(({ theme }) => ({
  borderRadius: 999,
  borderStyle: 'dashed',
  textTransform: 'none',
  height: 28,
  paddingInline: theme.spacing(1.25),
  color: theme.palette.text.secondary,
  borderColor: alpha(theme.palette.text.primary, 0.3),
  '&:hover': {
    borderStyle: 'dashed',
    borderColor: theme.palette.primary.main,
    color: theme.palette.text.primary
  }
}));

// An offer, never an action. It sits beside the Add filter button rather than
// replacing anything, and ignoring it costs nothing — Enter in the search box
// still does exactly what it always did.
const SuggestionButton = styled(Button)(({ theme }) => ({
  borderRadius: 999,
  textTransform: 'none',
  height: 28,
  paddingInline: theme.spacing(1.25),
  color: theme.palette.primary.main,
  backgroundColor: alpha(theme.palette.primary.main, 0.06)
}));

/**
 * @param {object} props
 * @param {Array} props.criteria
 * @param {Array} props.fields         served descriptors
 * @param {(c: object) => void} props.onAdd
 * @param {(i: number, c: object) => void} props.onUpdate
 * @param {(i: number) => void} props.onRemove
 * @param {(field: object) => Array} [props.optionsFor]
 * @param {Record<string,string>} [props.tenantLabels]
 * @param {(field: object) => Record<string,string>} [props.valueLabelsFor]
 * @param {object|null} [props.suggestion]  identifier recognised in the search box
 * @param {() => void} [props.onAcceptSuggestion]
 * @param {(s: string) => string} props.t
 */
export default function FamilySearchFilterBar({
  criteria,
  fields,
  onAdd,
  onUpdate,
  onRemove,
  optionsFor,
  tenantLabels,
  valueLabelsFor,
  suggestion,
  onAcceptSuggestion,
  t
}) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  // null = adding; a number = editing that index.
  const [editingIndex, setEditingIndex] = React.useState(null);
  const [expanded, setExpanded] = React.useState(false);

  const addRef = React.useRef(null);

  const fieldFor = React.useCallback(
    (key) => (fields || []).find((f) => f.key === key) || null,
    [fields]
  );

  const labelFor = React.useCallback(
    (field) => resolveFieldLabel(field, t, tenantLabels),
    [t, tenantLabels]
  );

  const openAdd = (event) => {
    setEditingIndex(null);
    setAnchorEl(event.currentTarget);
  };

  const openEdit = (index) => (event) => {
    setEditingIndex(index);
    setAnchorEl(event.currentTarget);
  };

  const close = () => setAnchorEl(null);

  const commit = (criterion) => {
    if (editingIndex === null) onAdd(criterion);
    // Editing REPLACES the criterion at that index rather than appending a
    // second one — the single most likely way to get this wrong, and the
    // reason the commit button changes its own label to "Update".
    else onUpdate(editingIndex, criterion);
    close();
  };

  const visible = expanded ? criteria : criteria.slice(0, OVERFLOW_AFTER);
  const overflowCount = criteria.length - visible.length;

  return (
    <Row data-testid="family-search-filter-bar">
      {visible.map((criterion, index) => {
        const field = fieldFor(criterion.field);
        const valueLabels = valueLabelsFor?.(field);
        const sentence = criterionSentence(criterion, field, t, {
          tenantLabels,
          valueLabels
        });
        const { shown } = truncateValue(
          valueText(criterion, field, t, valueLabels)
        );
        return (
          <FilterChip
            key={`${criterion.field}-${criterion.operator}-${index}`}
            fieldLabel={labelFor(field)}
            operatorLabel={t(OPERATOR_LABEL_KEYS[criterion.operator] || '')}
            valueLabel={shown}
            fullSentence={sentence}
            onEdit={openEdit(index)}
            onRemove={() => onRemove(index)}
            // Fully qualified, both of them. "Remove" on its own tells a
            // screen-reader user nothing about which of five chips they are
            // about to delete.
            removeLabel={`${t('Remove filter')}, ${sentence}`}
            editLabel={`${t('Edit filter')}, ${sentence}`}
          />
        );
      })}

      {overflowCount > 0 && (
        <Button
          size="small"
          onClick={() => setExpanded(true)}
          sx={{ textTransform: 'none', borderRadius: 999, height: 28 }}
        >
          {`+${overflowCount} ${t('more')}`}
        </Button>
      )}

      {suggestion ? (
        <SuggestionButton
          size="small"
          variant="text"
          startIcon={<LightbulbOutlinedIcon sx={{ fontSize: 16 }} />}
          onClick={onAcceptSuggestion}
        >
          {suggestion.label}
        </SuggestionButton>
      ) : null}

      <AddButton
        ref={addRef}
        variant="outlined"
        size="small"
        startIcon={<AddIcon sx={{ fontSize: 16 }} />}
        onClick={openAdd}
      >
        {t('Add filter')}
      </AddButton>

      <FilterBuilder
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        onCommit={commit}
        fields={fields}
        initial={editingIndex === null ? null : criteria[editingIndex]}
        optionsFor={optionsFor}
        labelFor={labelFor}
        t={t}
      />
    </Row>
  );
}
